import React, { ChangeEvent, useState } from "react";
import {
    LotTypes,
    PropertIdentifier,
    Provinces,
    RouteTypes,
    StreetDirections,
    StreetTypes,
    translations,
} from "../../entities/Enums";
import { Address } from "./AddressSearchComponent";
import { LOT_TYPE, PROPERTY_IDENTITFIER, UNIT_TYPE } from "./AddressSearchComponent";
import ErrorElement from "../../commonComponent/ErrorElement";

export interface AddressDetailsProps {
    id: string;
    addressDetails?: Address;
    lang: 'en' | 'fr';
    noStreetNoAddress: boolean;
    readOnly: boolean;
    errors?: {id: string, message:string}[];
    setAddressDetails: (address: Address) => void;
};

const AddressDetails = ({
    id,
    addressDetails,
    lang,
    noStreetNoAddress,
    errors,
    readOnly,
    setAddressDetails,
}: AddressDetailsProps) => {
    const handleIdentifierChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setAddressDetails({...addressDetails, identifier: e.target.value as PROPERTY_IDENTITFIER})
        }
    }

    return (
        <>
            {addressDetails?.postalCode && (
                <>
                    {noStreetNoAddress && (
                        <>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_lot_type_${id}`}>{lang === 'en' ? "Lot type(required)" : "Type de lot (obligatoire)"}</label>
                                {!readOnly && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'lotType').length} 
                                    message={errors.filter(err => err.id === 'lotType')?.[0]?.message}
                                    id={`fdIpt_lot_type_${id}_error`}
                                />}
                                <select
                                    className={`ontario-input ontario-dropdown ontario-input--7-char-width ${!!errors.filter(err => err.id === 'lotType').length ? 'ontario-input__error' : ''}`}
                                    id={`fdIpt_lot_type_${id}`}
                                    key={`fdIpt_lot_type_${id}`}
                                    value={addressDetails?.lotType}
                                    disabled={readOnly}
                                    onChange={(e) => {
                                        setAddressDetails({...addressDetails, lotType: e.target.value as LOT_TYPE});
                                    }}
                                >
                                    {LotTypes[lang.toUpperCase()].map((option) => (
                                        <option key={option.value} value={option.value} selected={option.value===(addressDetails?.lotType ?? '')}>{option.text}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_lot_num_${id}`}>
                                    {lang === 'en' ? "Lot/part number(required)" : "Numéro de lot ou de partie de lot(obligatoire)"}
                                </label>
                                <p>{lang === 'en' ? 'For example 4' : 'Par exemple : 4'}</p>
                                {!readOnly && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'lotNumber').length} 
                                    message={errors.filter(err => err.id === 'lotNumber')?.[0]?.message}
                                    id={`fdIpt_lot_num_${id}_error`}
                                />}
                                <input
                                    className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'lotNumber').length ? 'ontario-input__error' : ''}`}
                                    id={`fdIpt_lot_num_${id}`}
                                    key={`fdIpt_lot_num_${id}`}
                                    value={addressDetails?.lotNumber || ''}
                                    disabled={readOnly}
                                    onChange={(e) => {
                                        setAddressDetails({...addressDetails, lotNumber: e.target.value });
                                    }}
                                />
                            </div>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_prop_id_${id}`}>
                                    {lang === 'en' ? "Property identifier (select one)" : "Identifiant de la propriété (en sélectionner un)"}
                                </label>
                                {!readOnly && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'identifier').length} 
                                    message={errors.filter(err => err.id === 'identifier')?.[0]?.message}
                                />}
                                <div id="propid_radioButton_1" className="ontario-radios">
                                    <div className="ontario-radios__item" id="fdIpt_div_concession" title='Concession number options'>
                                        <input 
                                            id={`fdIpt_prop_id_concession_${id}`}
                                            className={`ontario-radios__input ${!!errors.filter(err => err.id === 'identifier').length ? 'ontario-input__error' : ''}`}
                                            type="radio"
                                            name="property_identifier"
                                            value={PropertIdentifier[lang.toUpperCase()][0].value}
                                            checked={addressDetails.identifier==='CN'}
                                            title={PropertIdentifier[lang.toUpperCase()][0].text}
                                            disabled={readOnly}
                                            onChange={handleIdentifierChange}
                                        />
                                        <label htmlFor={`fdIpt_prop_id_concession_${id}`} className="ontario-label ontario-radios__label">
                                            {PropertIdentifier[lang.toUpperCase()][0].text}
                                        </label>
                                    </div>
                                    <div className="ontario-radios__item" id="fdIpt_div_plan" title="Plan number option">
                                        <input 
                                            id={`fdIpt_prop_id_plan_${id}`}
                                            className={`ontario-radios__input ${!!errors.filter(err => err.id === 'identifier').length ? 'ontario-input__error' : ''}`}
                                            type="radio"
                                            name="property_identifier"
                                            value={PropertIdentifier[lang.toUpperCase()][1].value}
                                            title={PropertIdentifier[lang.toUpperCase()][1].text} 
                                            checked={addressDetails.identifier==='PN'}
                                            disabled
                                            onChange={handleIdentifierChange}
                                        />
                                        <label htmlFor="fdIpt_prop_id_plan" className="ontario-label ontario-radios__label">
                                            {PropertIdentifier[lang.toUpperCase()][1].text}
                                        </label>
                                    </div>
                                    <div>
                                        <p>{lang === 'en' ? 'For example 12345' : 'Par exemple : 12345'}</p>
                                        {!readOnly && <ErrorElement
                                            display={!!errors.filter(err => err.id === 'identifierNumber').length} 
                                            message={errors.filter(err => err.id === 'identifierNumber')?.[0]?.message}
                                            id={`fdIpt_identifier_number_${id}_error`}
                                        />}
                                        <input
                                            className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'identifierNumber').length ? 'ontario-input__error' : ''}`}
                                            id={'fdIpt_identifier_number_' + id}
                                            key={'fdIpt_identifier_number_' + id}
                                            value={addressDetails.identifierNumber || ''}
                                            required={true}
                                            disabled={readOnly}
                                            onChange={(e) => setAddressDetails({...addressDetails, identifierNumber: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_township_${id}`}>{lang === 'en' ? "Township (required)" : "Canton (obligatoire)"}</label>
                                <p>{lang === 'en' ? 'For example Algonquin Highlands' : 'Par exemple : Algonquin Highlands'}</p>
                                {!readOnly && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'township').length} 
                                    message={errors.filter(err => err.id === 'township')?.[0]?.message}
                                    id={`fdIpt_township_${id}_error`}
                                />}
                                <input
                                    className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'township').length ? 'ontario-input__error' : ''}`}
                                    id={`fdIpt_township_${id}`}
                                    key={`fdIpt_township_${id}`}
                                    value={addressDetails?.township || ''}
                                    disabled={readOnly}
                                    onChange={(e) => {
                                        setAddressDetails({...addressDetails, township: e.target.value });
                                    }}
                                />
                            </div>
                        </>
                    )}
                  <div style={{display: [1].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                        <label className="ontario-label" htmlFor={`fdIpt_unit_type_${id}`}>{lang === 'en' ? "Unit type (optional)" : "Type d’unité (facultatif)"}</label>
                        {!readOnly && <ErrorElement
                            display={!!errors.filter(err => err.id === 'unitType').length} 
                            message={errors.filter(err => err.id === 'unitType')?.[0]?.message}
                            id={`fdIpt_unit_type_${id}_error`}
                        />}
                        <select
                            className={`ontario-input ontario-dropdown ontario-input--7-char-width ${!!errors.filter(err => err.id === 'unitType').length ? 'ontario-input__error' : ''}`}
                            id={`fdIpt_unit_type_${id}`}
                            key={`fdIpt_unit_type_${id}`}
                            value={addressDetails.unitType || ''}
                            disabled={readOnly}
                            onChange={(e) => {
                                setAddressDetails({...addressDetails, unitType: e.target.value as UNIT_TYPE});
                            }}
                        >
                            {translations[lang.toUpperCase()].map((option) => (
                                <option key={option.value} value={option.value} selected={addressDetails.unitType===option.value}>{option.text}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{display: [1].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                        <label className="ontario-label" htmlFor={'fdIpt_unit_number_' + id}>{lang === 'en' ? "Unit number (optional)" : "Numéro d’unité (facultatif)"}</label>
                        {!readOnly && <ErrorElement
                            display={!!errors.filter(err => err.id === 'suiteNumber').length} 
                            message={errors.filter(err => err.id === 'suiteNumber')?.[0]?.message}
                            id={'fdIpt_unit_number_' + id + '_error'}
                        />}
                        <input
                            className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'suiteNumber').length ? 'ontario-input__error' : ''}`}
                            id={'fdIpt_unit_number_' + id}
                            key={'fdIpt_unit_number_' + id}
                            value={addressDetails?.suiteNumber || ''}
                            required={false}
                            disabled={readOnly}
                            onChange={(e) => setAddressDetails({...addressDetails, suiteNumber: e.target.value})}
                        >
                        </input>
                    </div>
                    {!noStreetNoAddress && (
                        <>
                            <div>
                                <label className="ontario-label" htmlFor={'fdIpt_street_number_' + id}>{lang === 'en' ? "Street number (required)" : "Numéro de rue (obligatoire)"}</label>
                                {!readOnly && <ErrorElement 
                                    display={!!errors.filter(err => err.id === 'streetNumber').length} 
                                    message={errors.filter(err => err.id === 'streetNumber')?.[0]?.message}
                                    id={'fdIpt_street_number_' + id + '_error'}
                                />}
                                <input
                                    className={`ontario-input ontario-input--10-char-width ${!!errors.filter(err => err.id === 'streetNumber').length ? 'ontario-input__error' : ''}`}
                                    id={'fdIpt_street_number_' + id}
                                    key={'fdIpt_street_number_' + id}
                                    value={addressDetails.streetNumber || ''}
                                    required={false}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({...addressDetails, streetNumber: e.target.value})}
                                />
                            </div>
                            <div style={{display: [1,2].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                                <label className="ontario-label" htmlFor={'fdIpt_street_number_suffix_' + id}>{lang === 'en' ? "Street number suffix (optional)" : "Suffixe du numéro de rue (facultatif)"}</label>
                                <p>{lang === 'en' ? 'For example A or 1/4' : 'Par exemple, A de 1/4'}</p>
                                <input
                                    className={'ontario-input ontario-input--5-char-width'}
                                    id={'fdIpt_street_number_suffix_' + id}
                                    key={'fdIpt_street_number_suffix_' + id}
                                    value={addressDetails?.streetNumberSuffix || ''}
                                    required={false}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({...addressDetails, streetFromSuffix: e.target.value})}
                                />
                            </div>
            
                            <div>
                                <label className="ontario-label" htmlFor={'fdIpt_street_name_' + id}>{lang === 'en' ? "Street name (required)" : "Nom de la rue (obligatoire)"}</label>
                                <p>{lang === 'en' ? 'For example Main' : 'Par exemple, Principale'}</p>
                                {!readOnly && <ErrorElement 
                                    display={!!errors.filter(err => err.id === 'streetName').length} 
                                    message={errors.filter(err => err.id === 'streetName')?.[0]?.message}
                                    id={'fdIpt_street_name_' + id + '_error'}
                                />}
                                <input
                                    className={'ontario-input  ontario-input--30-char-width'}
                                    id={'fdIpt_street_name_' + id}
                                    key={'fdIpt_street_name_' + id}
                                    value={addressDetails?.streetName || ''}
                                    required={false}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({...addressDetails, streetName: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="ontario-label" htmlFor={'fdIpt_street_type_' + id}>{lang === 'en' ? "Street Type (optional)" : "Type de rue (obligatoire)"}</label>
                                <p>{lang === 'en' ? 'For example Boulevard' : 'Par exemple, Boulevard'}</p>
                                <select
                                    className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                                    id={'fdIpt_street_type_' + id}
                                    key={'fdIpt_street_type_' + id}
                                    value={addressDetails?.streetType?.toUpperCase() || ''}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({ ...addressDetails, streetType: e.target.value })}
                                >
                                    {StreetTypes.map((option) => (
                                        <option key={option.value} value={option.value} selected={addressDetails?.streetType?.toLocaleUpperCase()===option.value}> {option.text}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="ontario-label" htmlFor={'fdIpt_street_direction_' + id}>{lang === 'en' ? "Street Direction (optional)" : "Direction de la rue (facultatif)"}</label>
                                <select
                                    className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                                    id={'fdIpt_street_direction_' + id}
                                    key={'fdIpt_street_direction_' + id}
                                    value={addressDetails?.streetDirection || ''}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({ ...addressDetails, streetDirection: e.target.value })}
                                >
                                    {StreetDirections[lang.toUpperCase()].map((option) => (
                                        <option key={option.value} value={option.value}> {option.text}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{display: [2,4].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                                <label className="ontario-label" htmlFor={'fdIpt_route_type_' + id}>{lang === 'en' ? "Route type (optional)" : "Type d’itinéraire (facultatif)"}</label>
                                {!readOnly && <ErrorElement 
                                    display={!!errors.filter(err => err.id === 'routeType').length} 
                                    message={errors.filter(err => err.id === 'routeType')?.[0]?.message}
                                    id={'fdIpt_route_type_' + id + '_error'}
                                />}
                                <select
                                    className={`ontario-input ontario-dropdown ontario-input--10-char-width ${!!errors.filter(err => err.id === 'routeType').length? 'ontario-input__error' : ''}`}
                                    id={'fdIpt_route_type_' + id}
                                    key={'fdIpt_route_type_' + id}
                                    value={addressDetails?.routeType || ''}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({ ...addressDetails, routeType: e.target.value })}
                                >
                                    {RouteTypes[lang.toUpperCase()].map((option) => (
                                        <option key={option.value} value={option.value} selected={addressDetails?.routeType===option.value}> {option.text}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{display: [2,4].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                                <label className="ontario-label" htmlFor={'fdIpt_routeNumber_' + id}>{lang === 'en' ? "Route number (optional)" : "Numéro de route (facultatif)"}</label>
                                <p>{lang === 'en' ? 'For example 30' : 'Par exemple : 30'}</p>
                                {!readOnly && <ErrorElement 
                                    display={!!errors.filter(err => err.id === 'routeNumber').length} 
                                    message={errors.filter(err => err.id === 'routeNumber')?.[0]?.message}
                                    id={'fdIpt_routeNumber_' + id + '_error'}
                                />}
                                <input
                                    className={`ontario-input ontario-input--5-char-width ${!!errors.filter(err => err.id === 'routeNumber').length? 'ontario-input__error' : ''}`}
                                    id={'fdIpt_routeNumber_' + id}
                                    key={'fdIpt_routeNumber_' + id}
                                    value={addressDetails?.routeNumber || ''}
                                    required={true}
                                    disabled={readOnly}
                                    onChange={(e) => setAddressDetails({...addressDetails, routeNumber: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    <div>
                        <label className="ontario-label" htmlFor={'fdIpt_manicipality_' + id}>{lang === 'en' ? "Municipality (city, town, etc.) (required)" : "Ville (obligatoire)"}</label>
                        {/* <p>{lang === 'en' ? 'For example Toronto' : 'Par exemple : Toronto'}</p> */}
                        {!readOnly && <ErrorElement 
                            display={!!errors.filter(err => err.id === 'municipality').length} 
                            message={errors.filter(err => err.id === 'municipality')?.[0]?.message}
                            id={'fdIpt_manicipality_' + id + '_error'}
                        />}
                        <input
                            className={`ontario-input ontario-input--30-char-width ${!!errors.filter(err => err.id === 'municipality').length ? 'ontario-input__error' : ''}`}
                            id={'fdIpt_manicipality_' + id}
                            key={'fdIpt_manicipality_' + id}
                            value={addressDetails?.municipality || ''}
                            required={true}
                            readOnly
                            onChange={(e) => setAddressDetails({...addressDetails, municipality: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="ontario-label" htmlFor={'fdIpt_province_' + id}>{lang === 'en' ? "Province (required)" : "Province (obligatoire)"}</label>
                        {/* <p>{lang === 'en' ? 'For example Ontario' : 'Par exemple : Ontario'}</p> */}
                        {!readOnly && <ErrorElement 
                            display={!!errors.filter(err => err.id === 'province').length} 
                            message={errors.filter(err => err.id === 'province')?.[0]?.message}
                            id={'fdIpt_province_' + id + '_error'}
                        />}
                        <select
                            className={`ontario-input ontario-dropdown ontario-input--10-char-width ${!!errors.filter(err => err.id === 'province').length ? 'ontario-input__error' : ''}`}
                            id={'fdIpt_province_' + id}
                            key={'fdIpt_province_' + id}
                            value={addressDetails.province || ''}
                            disabled
                            aria-readonly
                            onChange={(e) => setAddressDetails({...addressDetails, province: e.target.value})}
                        >
                            {Provinces[lang.toUpperCase()].map((option) => (
                                <option disabled key={option.value} value={option.value} selected={addressDetails?.province===option.value}> {option.text}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="ontario-label" htmlFor={'fdIpt_postalCode_' + id}>{lang === 'en' ? "Postal code (required)" : "Code postal (obligatoire)"}</label>
                        {/* <p>{lang === 'en' ? 'For example A1A 1A1' : 'Par exemple : A1A 1A1'}</p> */}
                        {!readOnly && <ErrorElement 
                            display={!!errors.filter(err => err.id === 'postalCode').length} 
                            message={errors.filter(err => err.id === 'postalCode')?.[0]?.message}
                            id={'fdIpt_postalCode_' + id + '_error'}
                        />}
                        <input
                            className={`ontario-input ontario-input--10-char-width ${!!errors.filter(err => err.id === 'postalCode').length ? 'ontario-input__error': ''}`}
                            id={'fdIpt_postalCode_' + id}
                            key={'fdIpt_postalCode_' + id}
                            value={addressDetails.postalCode || ''}
                            required={true}
                            readOnly={true}
                            onChange={(e) => setAddressDetails({ ...addressDetails, postalCode: e.target.value })}
                        />
                    </div>
                </>
            )}
        </>
    );
}

export default AddressDetails;